import * as Log from 'loglevel'
import * as PropTypes from 'prop-types'
import * as React from 'react'
import * as Rx from 'rxjs/Rx'
import createHistory from 'history/createBrowserHistory'
import { Action, applyMiddleware, compose, createStore } from 'redux'
import { ActionsObservable, createEpicMiddleware } from 'redux-observable'
import { ConnectedRouter, routerMiddleware } from 'react-router-redux'
import { Helmet } from 'react-helmet'
import { History } from 'history'
import { Provider, Store } from 'react-redux'
import { Route } from 'react-router'

import * as packageData from '../../package.json'
import AppContext from './AppContext'
import CredentialsStorage from '../io/CredentialsStorage'
import Dependencies from '../epics/Dependencies'
import Dialogue from './Dialogue'
import Header from './Header'
import InfoModal from './InfoModal'
import MediaInput from './MediaInput'
import PhotoModal from './PhotoModal'
import SignUpPromoteModal from './SignUpPromoteModal'
import State from '../states'
import Toolbar from './Toolbar'
import World from './World'
import reducer from '../reducers'
import rootEpic from '../epics'
import { APIClient } from '../io/api-client'
import { infoRoute, photoRoute } from '../states/RouterState'

export interface Props {
  mobileVersion?: string
}

export default class App extends React.Component<Props> {
  static childContextTypes = {
    apiClient: PropTypes.instanceOf(APIClient).isRequired,
    appContext: PropTypes.any.isRequired
  }

  private readonly logger: Log.Logger
  private readonly store: Store<State>
  private readonly apiClient: APIClient
  private readonly credentialsStorage: CredentialsStorage
  private readonly appContext: AppContext = {
    clientSession: null
  }
  private readonly history: History

  constructor (props: Props, context?: any) {
    super(props, context)

    this.history = createHistory()
    Log.enableAll()
    this.logger = Log.getLogger('App')

    this.apiClient = new APIClient(
      process.env.REACT_APP_API_URL!,
      (packageData as any).version
    )
    this.credentialsStorage = new CredentialsStorage()

    const dependencies: Dependencies = {
      apiClient: this.apiClient,
      appContext: this.appContext,
      credentialsStorage: this.credentialsStorage,
      logger: Log.getLogger('epics'),
      mobileVersion: props.mobileVersion,
      scheduler: Rx.Scheduler.asap
    }
    const epicMiddleware = createEpicMiddleware(rootEpic, {
      adapter: {
        input: action$ =>
          // So that we will have ofFSAType available
          new ActionsObservable(action$),
        output: (action$: Rx.Observable<Action>) =>
          action$.catch(error => {
            if (process.env.NODE_ENV === 'production') {
              Bugsnag.notifyException(error, 'Broken Epics Pipeline')
            } else {
              // throw error to make it print in console
              // we are doing this now just to make the error more visible.
              // otherwise when epic emits error, the root epic will be canceled
              // but no error will be printed.
              // I think we should ensure epic is handling all error, no error
              // should be emited.
              // for more info please reference to:
              // https://github.com/redux-observable/redux-observable/issues/94
              setTimeout(() => {
                throw error
              }, 0)
            }

            // bubble the error all the way up and terminate all epics
            // subscription.
            // TODO: maybe we should consider retry, keep the app up and
            // running as much as possible?
            return Rx.Observable.throw(error)
          })
      },
      dependencies
    })

    const anyWindow: any = window
    let composeEnhancers = compose
    if (process.env.NODE_ENV !== 'production') {
      composeEnhancers =
        typeof anyWindow === 'object' &&
        anyWindow.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
          ? anyWindow.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
              maxAge: 1024
            })
          : compose
    }
    this.store = createStore(
      reducer,
      composeEnhancers(
        applyMiddleware(epicMiddleware, routerMiddleware(this.history))
      )
    )

    if (process.env.NODE_ENV === 'production') {
      this.history.listen(location => {
        const anyWindow = window as any
        if (anyWindow.ga) {
          anyWindow.ga('set', 'page', location.pathname)
          anyWindow.ga('send', 'pageview')
        }
      })
    }
  }

  getChildContext () {
    return { apiClient: this.apiClient, appContext: this.appContext }
  }

  render () {
    if (this.props.mobileVersion) {
      return (
        <Provider store={this.store}>
          <ConnectedRouter history={this.history}>
            <div className='react-wrapper'>
              <World />
            </div>
          </ConnectedRouter>
        </Provider>
      )
    }
    return (
      <Provider store={this.store}>
        <ConnectedRouter history={this.history}>
          <div className='react-wrapper'>
            <Helmet>
              <title>
                TipWander - Explore the world with your finger tips!
              </title>
            </Helmet>
            <World />
            <Dialogue />
            <MediaInput />
            <Toolbar />
            <Header />
            <Route path={photoRoute.path} component={PhotoModal} />
            <Route path={infoRoute.path} component={InfoModal} />
            <SignUpPromoteModal />
          </div>
        </ConnectedRouter>
      </Provider>
    )
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/App.tsx